/**
 * Auth State Storage Domain
 * Manages authentication state persistence
 * @module @services/storage
 */

import type { AuthState } from '@shared/types';
import { defaultAdapters, AUTH_TOKEN_KEY, clearLocalStorageExcept } from '@infra/storage';
import { createStorageConfig, STORAGE_KEYS } from './storage-config';
import { environment } from '@shared/config';
import { I18N_LANGUAGE_KEY } from '@infra/i18n';
import { readStoredTokens, stopTokenRefreshScheduler, withAuthRefreshLock } from '@infra/auth';
import { disconnectCoreSocket } from '../core/realtime';

/** Why an auth session ended — drives the copy/redirect `LogoutCallback` applies. */
export type SessionEndTeardownReason =
  | 'logout'
  | 'superseded'
  | 'expired'
  | 'deactivated'
  | 'deleted'
  | 'linkMismatch'
  | 'passwordChanged';

/** sessionStorage key `LogoutCallback` reads (and deletes) after the FusionAuth round trip. */
const END_REASON_KEY = 'auth:end-reason';

// endSession() now has 3 independent callers (manual logout, SessionRevokedGuard's
// kick/expired modal, App.tsx's stale-token cleanup) that can plausibly fire close
// together (e.g. a kick arrives while the stale-token check is also running). Without
// this, both would race to POST /v1/auth/logout and set window.location.href — harmless
// (same destination) but wasteful. Never reset: a real navigation away ends the module's
// lifetime anyway, so there's nothing to recover into.
let endingSession = false;

const { key: STORAGE_KEY, adapter } = createStorageConfig(STORAGE_KEYS.AUTH_STATE);
const tokenAdapter = defaultAdapters.local;
const DEFAULT_AUTH_STATE: AuthState = {
  isAuthenticated: false,
  role: null,
};

/**
 * Cap the logout revoke request. It runs INSIDE the shared `auth-token-refresh` Web Lock,
 * so a hung network would otherwise hold that lock for the full browser fetch timeout and
 * block every other tab's refresh. Matches REFRESH_TIMEOUT_MS on the refresh path.
 */
const LOGOUT_REVOKE_TIMEOUT_MS = 15_000;

/**
 * Get current auth state
 */
export function getAuthState(): AuthState {
  // No migration needed for the userId→userPublicId rename: AuthState no longer stores the id
  // (getCurrentUser derives it from the token), so an old persisted `{ isAuthenticated, userId,
  // role }` is read as `{ isAuthenticated, role }` — its stale `userId` is simply ignored.
  return adapter.getItem(STORAGE_KEY, DEFAULT_AUTH_STATE);
}

/**
 * Set auth state (overwrites)
 */
export function setAuthState(state: AuthState): void {
  adapter.setItem(STORAGE_KEY, state);
}

/**
 * The single wipe site for local auth artifacts. Idempotent — safe to call from
 * `LogoutCallback` on every visit, including a direct hit or a re-visit after a
 * broken round trip. Order matters: remove `AUTH_TOKEN_KEY` FIRST (its own
 * key-specific `storage` event is the cross-tab de-auth signal sibling tabs listen
 * for) before the wholesale `clear()`, which only fires a `key === null` event.
 * `clear()` also wipes `AUTH_STATE_KEY`, so re-seed it after.
 */
export function clearLocalAuthArtifacts(): void {
  tokenAdapter.removeItem(AUTH_TOKEN_KEY);
  clearLocalStorageExcept([I18N_LANGUAGE_KEY]);
  setAuthState(DEFAULT_AUTH_STATE);
}

/**
 * Tear down this device's session and hand off to FusionAuth end-session, which is
 * the only thing that actually kills the browser's SSO cookie. Used by every de-auth
 * path (manual logout, "signed in elsewhere" kick, expired refresh, stale token) so
 * pressing "Login" afterwards always re-shows the SSO credential form instead of
 * silently re-authenticating off a still-live SSO cookie.
 *
 * Does NOT wipe localStorage — that happens once, unconditionally, in
 * `LogoutCallback` after the FusionAuth round trip returns. Only the `token` key is
 * removed here, as a fail-safe in case the round trip never completes.
 */
export async function endSession(reason: SessionEndTeardownReason): Promise<void> {
  if (endingSession) return;
  endingSession = true;

  stopTokenRefreshScheduler();

  // R3: proactively drop the core socket (mirrors SessionRevokedGuard.handleOk) so a
  // falsy logoutUrl — i.e. no navigation below — doesn't leave the socket hanging.
  disconnectCoreSocket();

  let logoutUrl = environment.logoutUrl;

  // R1: serialize the whole read→revoke→clear against token refreshes (same cross-tab
  // Web Lock the refresh path uses). Prevents a refresh in flight from rotating the token
  // we're about to revoke (leaving a live sibling session), and prevents it from writing
  // fresh tokens back right after we clear storage (resurrection).
  await withAuthRefreshLock(async () => {
    const tokens = readStoredTokens();

    // Try to revoke tokens at backend and get logout URL from response. Aborted after
    // LOGOUT_REVOKE_TIMEOUT_MS so a hung network can't hold the auth-refresh lock.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), LOGOUT_REVOKE_TIMEOUT_MS);
    try {
      const response = await fetch(`${environment.apiUrl}/v1/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: tokens?.access_token,
          refresh_token: (tokens as Record<string, unknown>)?.refresh_token,
          id_token: tokens?.id_token,
        }),
        signal: controller.signal,
      });
      if (response.ok) {
        const data = await response.json();
        if (data?.url) {
          logoutUrl = data.url;
        }
      }
    } catch (error) {
      // Includes AbortError on timeout — continue with logout even if revocation fails.
      console.warn('Token revocation failed:', error);
    } finally {
      clearTimeout(timeoutId);
    }

    // Fail-safe only — NOT the wipe. If the FusionAuth round trip below never
    // returns, the app must still consider itself logged out. The full wipe
    // (auth state, user_profile, leftovers) happens once in LogoutCallback.
    tokenAdapter.removeItem(AUTH_TOKEN_KEY);
  });

  // Survives the FusionAuth round trip: sessionStorage is per-tab/per-origin and the
  // redirect below returns to the same tab + origin. LogoutCallback reads + deletes it.
  sessionStorage.setItem(END_REASON_KEY, reason);

  // Redirect to FusionAuth logout
  if (logoutUrl) {
    window.location.href = logoutUrl;
  }
}

/** Reads and deletes the end-reason marker `endSession` leaves for `LogoutCallback`. */
export function consumeSessionEndReason(): SessionEndTeardownReason | null {
  const reason = sessionStorage.getItem(END_REASON_KEY) as SessionEndTeardownReason | null;
  sessionStorage.removeItem(END_REASON_KEY);
  return reason;
}

/**
 * Logout flow (revoke tokens + end the FusionAuth SSO session).
 * Kept as a thin alias so existing TopBar/Sidebar callers (`await logout()`) are unchanged.
 */
export const logout = (): Promise<void> => endSession('logout');
